import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import api, { formatApiError } from "@/lib/api";
import { Spinner } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Sparkles, Save, Globe, Eye, Image as ImageIcon, Plus, Trash2, Wand2, Clock, ArrowLeft, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";

const QUILL_MODULES = { toolbar: [[{ header: [2, 3, false] }], ["bold", "italic"], [{ list: "ordered" }, { list: "bullet" }], ["blockquote", "link"], ["clean"]] };
const scoreColor = (s) => (s >= 80 ? "text-[#3f9d5c]" : s >= 60 ? "text-[#8a6100]" : "text-kid-coral");
const CTA_TYPES = [{ v: "soft", l: "Soft CTA" }, { v: "funding", l: "Funding CTA" }, { v: "newsletter", l: "Newsletter CTA" }];

export default function ArticleEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [a, setA] = useState(null);
  const [cats, setCats] = useState([]);
  const [busy, setBusy] = useState("");
  const [audit, setAudit] = useState(null);
  const [schedDate, setSchedDate] = useState("");

  const load = async () => {
    const [art, c] = await Promise.all([api.get(`/admin/articles/${id}`), api.get("/article-categories")]);
    setA(art.data); setCats(c.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  if (!a) return <Spinner />;

  const set = (k, v) => setA({ ...a, [k]: v });

  const save = async (silent) => {
    setBusy("save");
    try {
      const payload = (({ title, subtitle, slug, excerpt, body_html, category_id, tags, primary_keyword, secondary_keywords,
        meta_title, meta_description, canonical_url, og_title, og_description, og_image_url, header_image_url, header_image_alt,
        header_image_source, cta_type, show_disclaimer, faqs, related_links }) => ({
        title, subtitle, slug, excerpt, body_html, category_id, tags: tags || [], primary_keyword, secondary_keywords: secondary_keywords || [],
        meta_title, meta_description, canonical_url, og_title, og_description, og_image_url, header_image_url, header_image_alt,
        header_image_source, cta_type, show_disclaimer, faqs: faqs || [], related_links: related_links || [],
      }))(a);
      const { data } = await api.put(`/admin/articles/${id}`, payload);
      setA(data);
      if (!silent) toast.success("Saved.");
      return data;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const ai = async (key, path, msg, after) => {
    setBusy(key);
    try {
      await save(true);
      const { data } = await api.post(`/admin/articles/${id}/${path}`);
      toast.success(msg);
      if (after) after(data); else await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const genImage = async () => {
    setBusy("image");
    try {
      await save(true);
      let prompt = a.ai_image_prompt;
      if (!prompt) { const { data } = await api.post(`/admin/articles/${id}/generate-image-prompt`); prompt = data.image_prompt; setA((p) => ({ ...p, ai_image_prompt: prompt, header_image_alt: p.header_image_alt || data.alt_text })); }
      toast.info("Generating image… this can take ~20s.");
      const { data } = await api.post(`/admin/articles/${id}/generate-image`, { prompt, alt_text: a.header_image_alt });
      await load();
      toast.success("Header image generated.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const runAudit = async () => {
    setBusy("audit");
    try { await save(true); const { data } = await api.post(`/admin/articles/${id}/seo-audit`); setAudit(data); await load(); toast.success("Audit complete."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const publish = async () => { await save(true); try { const { data } = await api.post(`/admin/articles/${id}/publish`); setA(data); toast.success("Published!"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const schedule = async () => { if (!schedDate) { toast.error("Pick a date/time."); return; } await save(true); try { const { data } = await api.post(`/admin/articles/${id}/schedule`, { scheduled_at: new Date(schedDate).toISOString() }); setA(data); toast.success("Scheduled."); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const imgUrl = a.header_image_url && a.header_image_url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${a.header_image_url}` : a.header_image_url;
  const barBtn = "kid-btn kid-btn-ghost h-9 px-3 text-xs w-full justify-start";

  const setFaq = (i, k, v) => { const f = [...(a.faqs || [])]; f[i] = { ...f[i], [k]: v }; set("faqs", f); };

  return (
    <div data-testid="article-editor">
      <div className="flex items-center gap-3 mb-5">
        <button onClick={() => navigate("/ops/article-studio")} className="text-kid-muted hover:text-kid-navy" data-testid="editor-back"><ArrowLeft size={20} /></button>
        <div className="flex-1 min-w-0"><h1 className="font-display text-2xl font-extrabold text-kid-navy truncate">{a.title || "Untitled"}</h1><p className="text-xs text-kid-muted">Status: <span className="capitalize font-semibold">{a.status}</span> · /articles/{a.slug}</p></div>
        <button onClick={() => save()} disabled={busy === "save"} className="kid-btn kid-btn-ghost h-10 px-4" data-testid="editor-save"><Save size={16} /> {busy === "save" ? "Saving…" : "Save"}</button>
        <button onClick={() => navigate(`/ops/article-studio/preview/${id}`)} className="kid-btn kid-btn-ghost h-10 px-4" data-testid="editor-preview"><Eye size={16} /> Preview</button>
        <button onClick={publish} className="kid-btn kid-btn-primary h-10 px-5" data-testid="editor-publish"><Globe size={16} /> Publish</button>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        {/* MAIN */}
        <div className="lg:col-span-2 space-y-4">
          <div className="kid-card p-5 space-y-3">
            <div className="space-y-1.5"><Label>Title</Label><Input value={a.title || ""} onChange={(e) => set("title", e.target.value)} data-testid="editor-title" /></div>
            <div className="space-y-1.5"><Label>Subtitle</Label><Input value={a.subtitle || ""} onChange={(e) => set("subtitle", e.target.value)} data-testid="editor-subtitle" /></div>
          </div>

          <div className="kid-card p-5">
            <Label className="mb-2 block">Article body</Label>
            <ReactQuill theme="snow" value={a.body_html || ""} onChange={(v) => set("body_html", v)} modules={QUILL_MODULES} data-testid="editor-body" />
          </div>

          {/* FAQ */}
          <div className="kid-card p-5" data-testid="editor-faq-section">
            <div className="flex items-center justify-between mb-3"><Label>FAQ</Label>
              <button onClick={() => ai("faq", "generate-faq", "FAQ generated.")} disabled={busy === "faq"} className="kid-btn kid-btn-ghost h-9 px-3 text-xs" data-testid="editor-gen-faq"><Sparkles size={14} /> {busy === "faq" ? "…" : "Generate FAQ"}</button>
            </div>
            <div className="space-y-3">
              {(a.faqs || []).map((f, i) => (
                <div key={i} className="border border-kid-line rounded-xl p-3 space-y-2" data-testid={`editor-faq-${i}`}>
                  <div className="flex gap-2"><Input value={f.question || ""} onChange={(e) => setFaq(i, "question", e.target.value)} placeholder="Question" /><button onClick={() => set("faqs", a.faqs.filter((_, x) => x !== i))} className="text-kid-coral p-2"><Trash2 size={16} /></button></div>
                  <textarea value={f.answer || ""} onChange={(e) => setFaq(i, "answer", e.target.value)} rows={2} placeholder="Answer" className="w-full rounded-lg border border-kid-line p-2 text-sm" />
                </div>
              ))}
              <button onClick={() => set("faqs", [...(a.faqs || []), { question: "", answer: "" }])} className="kid-btn kid-btn-ghost h-9 px-3 text-xs" data-testid="editor-add-faq"><Plus size={14} /> Add FAQ</button>
            </div>
          </div>

          {/* Related links */}
          <div className="kid-card p-5" data-testid="editor-related-section">
            <div className="flex items-center justify-between mb-3"><Label>Related links (internal)</Label>
              <button onClick={() => ai("links", "suggest-links", "Suggestions ready.", (d) => set("related_links", [...(a.related_links || []), ...(d.links || [])]))} disabled={busy === "links"} className="kid-btn kid-btn-ghost h-9 px-3 text-xs" data-testid="editor-suggest-links"><Wand2 size={14} /> {busy === "links" ? "…" : "Suggest links"}</button>
            </div>
            <div className="space-y-2">
              {(a.related_links || []).map((l, i) => (
                <div key={i} className="flex items-center gap-2 text-sm" data-testid={`editor-related-${i}`}>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-kid-mist text-kid-muted">{l.type || "custom"}</span>
                  <span className="flex-1 text-kid-navy truncate">{l.anchor || l.title} → {l.target || l.url}</span>
                  <button onClick={() => set("related_links", a.related_links.filter((_, x) => x !== i))} className="text-kid-coral p-1"><Trash2 size={14} /></button>
                </div>
              ))}
              {(!a.related_links || a.related_links.length === 0) && <p className="text-xs text-kid-muted">No related links yet.</p>}
            </div>
          </div>
        </div>

        {/* SIDEBAR */}
        <div className="space-y-4">
          {/* Readiness */}
          <div className="kid-card p-5" data-testid="editor-scores">
            <div className="flex items-center justify-between"><Label>Article readiness</Label><span className={`text-2xl font-extrabold ${scoreColor(a.article_readiness_score || 0)}`} data-testid="editor-readiness-score">{a.article_readiness_score || 0}</span></div>
            <div className="grid grid-cols-2 gap-2 text-xs mt-3">
              {[["SEO", a.seo_score], ["Readability", a.readability_score], ["Inclusivity", a.inclusivity_score], ["Completeness", a.completeness_score], ["Metadata", a.metadata_score]].map(([l, s]) => (
                <div key={l} className="flex justify-between border border-kid-line rounded-lg px-2 py-1"><span className="text-kid-muted">{l}</span><span className={`font-bold ${scoreColor(s || 0)}`}>{s || 0}</span></div>
              ))}
            </div>
            <button onClick={runAudit} disabled={busy === "audit"} className="kid-btn kid-btn-ghost h-9 px-3 text-xs w-full mt-3" data-testid="editor-run-audit"><ClipboardCheck size={14} /> {busy === "audit" ? "Auditing…" : "Run AI editorial audit"}</button>
            {a.read_time_minutes ? <p className="text-xs text-kid-muted mt-2 flex items-center gap-1"><Clock size={12} /> {a.read_time_minutes} min read</p> : null}
          </div>

          {audit && (
            <div className="kid-card p-5" data-testid="editor-audit-flags">
              <Label className="mb-2 block">Editorial flags</Label>
              {(audit.flags || []).length === 0 ? <p className="text-xs text-[#3f9d5c]">No issues flagged. 🎉</p> : (
                <div className="space-y-2">{audit.flags.map((f, i) => (
                  <div key={i} className="text-xs border-l-2 pl-2" style={{ borderColor: f.severity === "high" ? "#FF7A73" : f.severity === "medium" ? "#FFC24B" : "#5AB4EE" }}>
                    <div className="font-semibold text-kid-navy">{f.issue}</div><div className="text-kid-muted">{f.suggestion}</div>
                  </div>))}
                </div>
              )}
            </div>
          )}

          {/* AI assistant */}
          <div className="kid-card p-5 space-y-2" data-testid="editor-ai-panel">
            <Label className="mb-1 block">AI assistant</Label>
            <button onClick={() => ai("draft", "generate-draft", "Draft regenerated.")} disabled={busy === "draft"} className={barBtn} data-testid="editor-gen-draft"><Sparkles size={14} /> {busy === "draft" ? "Writing…" : "Regenerate draft"}</button>
            <button onClick={() => ai("meta", "generate-metadata", "Metadata generated.")} disabled={busy === "meta"} className={barBtn} data-testid="editor-gen-meta"><Sparkles size={14} /> {busy === "meta" ? "…" : "Generate SEO metadata"}</button>
          </div>

          {/* Header image */}
          <div className="kid-card p-5 space-y-3" data-testid="editor-image">
            <Label>Header image</Label>
            {imgUrl ? <img src={imgUrl} alt={a.header_image_alt || ""} className="w-full aspect-[16/9] object-cover rounded-xl" data-testid="editor-image-preview" /> : <div className="w-full aspect-[16/9] rounded-xl bg-kid-mist grid place-items-center text-kid-muted"><ImageIcon size={28} /></div>}
            <div className="space-y-1.5"><Label className="text-xs">AI image prompt</Label><textarea value={a.ai_image_prompt || ""} onChange={(e) => set("ai_image_prompt", e.target.value)} rows={3} className="w-full rounded-lg border border-kid-line p-2 text-xs" placeholder="Leave blank to auto-generate a prompt" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Alt text</Label><Input value={a.header_image_alt || ""} onChange={(e) => set("header_image_alt", e.target.value)} data-testid="editor-image-alt" /></div>
            <button onClick={genImage} disabled={busy === "image"} className="kid-btn kid-btn-primary h-10 px-4 w-full" data-testid="editor-gen-image"><Sparkles size={15} /> {busy === "image" ? "Generating…" : "Generate AI image"}</button>
          </div>

          {/* SEO + settings */}
          <div className="kid-card p-5 space-y-3" data-testid="editor-seo">
            <Label>SEO & settings</Label>
            <div className="space-y-1.5"><Label className="text-xs">Slug</Label><Input value={a.slug || ""} onChange={(e) => set("slug", e.target.value)} data-testid="editor-slug" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Category</Label>
              <select value={a.category_id || ""} onChange={(e) => set("category_id", e.target.value)} className="w-full h-11 rounded-xl border border-kid-line px-3 text-sm" data-testid="editor-category">
                <option value="">Select…</option>{cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Primary keyword</Label><Input value={a.primary_keyword || ""} onChange={(e) => set("primary_keyword", e.target.value)} data-testid="editor-keyword" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Meta title <span className="text-kid-muted">({(a.meta_title || "").length}/60)</span></Label><Input value={a.meta_title || ""} onChange={(e) => set("meta_title", e.target.value)} data-testid="editor-meta-title" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Meta description <span className="text-kid-muted">({(a.meta_description || "").length}/160)</span></Label><textarea value={a.meta_description || ""} onChange={(e) => set("meta_description", e.target.value)} rows={2} className="w-full rounded-lg border border-kid-line p-2 text-sm" data-testid="editor-meta-desc" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Excerpt</Label><textarea value={a.excerpt || ""} onChange={(e) => set("excerpt", e.target.value)} rows={2} className="w-full rounded-lg border border-kid-line p-2 text-sm" /></div>
            <div className="space-y-1.5"><Label className="text-xs">CTA type</Label>
              <select value={a.cta_type || "soft"} onChange={(e) => set("cta_type", e.target.value)} className="w-full h-11 rounded-xl border border-kid-line px-3 text-sm" data-testid="editor-cta-type">
                {CTA_TYPES.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
              </select>
            </div>
            <div className="flex items-center justify-between pt-1"><Label className="text-xs">Show disclaimer</Label><Switch checked={a.show_disclaimer !== false} onCheckedChange={(v) => set("show_disclaimer", v)} data-testid="editor-disclaimer-toggle" /></div>
          </div>

          {/* Schedule */}
          <div className="kid-card p-5 space-y-2" data-testid="editor-schedule">
            <Label className="text-xs">Schedule publish</Label>
            <input type="datetime-local" value={schedDate} onChange={(e) => setSchedDate(e.target.value)} className="w-full h-11 rounded-xl border border-kid-line px-3 text-sm" data-testid="editor-schedule-date" />
            <button onClick={schedule} className="kid-btn kid-btn-ghost h-9 px-3 text-xs w-full" data-testid="editor-schedule-btn">Schedule</button>
          </div>
        </div>
      </div>
    </div>
  );
}
